import gql from 'graphql-tag'
import {Graphql} from '@hc/graphql'
import {STORAGE_UPLOAD_CREATE_MUTATION, STORAGE_UPLOAD_COMPLETE_MUTATION} from '@hc/base'
import type {AssetDto, UploadTicket} from '@hc/base'
import {resolvePostTarget} from './upload_target'
import type {PostTarget} from './upload_target'

export interface UploadTransport {
  createTicket(input: {policy: string, filename: string, mime_type: string, size: number}): Promise<UploadTicket>
  postFile(ticket: UploadTicket, file: File, onProgress: (percent: number) => void, signal: AbortSignal): Promise<void>
  complete(assetId: string): Promise<AssetDto>
}

interface GraphqlErrorLike {
  message: string
}

function graphqlError(error: unknown): Error {
  const err = error as {graphQLErrors?: GraphqlErrorLike[], message?: string}
  if (err?.graphQLErrors?.length) return new Error(err.graphQLErrors[0].message)
  return new Error(err?.message || 'Request failed')
}

async function mutate<T>(mutation: string, variables: Record<string, unknown>, field: string): Promise<T> {
  try {
    const res = await Graphql.apollo.mutate<Record<string, T>>({
      mutation: gql(mutation),
      variables,
      fetchPolicy: 'no-cache',
    })
    if (res.errors?.length) throw new Error(res.errors[0].message)
    if (!res.data || !res.data[field]) throw new Error('Empty response')
    return res.data[field]
  } catch (e) {
    throw graphqlError(e)
  }
}

function readableHttpError(xhr: XMLHttpRequest): string {
  const text = xhr.responseText || ''
  try {
    const json = JSON.parse(text) as {message?: string | string[]}
    if (json?.message) return Array.isArray(json.message) ? json.message.join(', ') : json.message
  } catch {
    // not json
  }
  const message = /<Message>([^<]*)<\/Message>/.exec(text)
  const code = /<Code>([^<]*)<\/Code>/.exec(text)
  if (code && code[1] === 'EntityTooLarge') return 'File is too large'
  if (message && message[1]) return message[1]
  return `HTTP ${xhr.status}`
}

export function createGraphqlUploadTransport(): UploadTransport {
  return {
    createTicket(input) {
      return mutate<UploadTicket>(STORAGE_UPLOAD_CREATE_MUTATION, input, 'storage_upload_create')
    },

    async postFile(ticket, file, onProgress, signal) {
      // lazy, the app store pulls in the whole app bootstrap
      const app = ticket.mode === 'server' ? (await import('../../app')).useAppStore() : null
      return new Promise<void>((resolve, reject) => {
        if (signal.aborted) return reject(new Error('Upload cancelled'))
        const xhr = new XMLHttpRequest()
        const form = new FormData()
        // S3 requires all policy fields before the file
        let target: PostTarget
        try {
          target = resolvePostTarget(ticket, path => app!.config.serviceUrl('core', path), app ? app.auth.jwt : '')
        } catch (e) {
          return reject(e)
        }
        for (const [name, value] of Object.entries(target.fields)) form.append(name, value)
        form.append('file', file)

        const onAbort = () => xhr.abort()
        signal.addEventListener('abort', onAbort)
        const done = () => signal.removeEventListener('abort', onAbort)

        xhr.upload.onprogress = (event: ProgressEvent) => {
          if (event.lengthComputable && event.total > 0) onProgress((event.loaded / event.total) * 100)
        }
        xhr.onload = () => {
          done()
          if (xhr.status >= 200 && xhr.status < 300) resolve()
          else reject(new Error(readableHttpError(xhr)))
        }
        xhr.onerror = () => {
          done()
          reject(new Error('Network error'))
        }
        xhr.onabort = () => {
          done()
          reject(new Error('Upload cancelled'))
        }
        xhr.open('POST', target.url)
        for (const [name, value] of Object.entries(target.headers)) xhr.setRequestHeader(name, value)
        xhr.send(form)
      })
    },

    complete(assetId) {
      return mutate<AssetDto>(STORAGE_UPLOAD_COMPLETE_MUTATION, {asset_id: assetId}, 'storage_upload_complete')
    },
  }
}
